import { fixtureMatchHidden, FIXTURE_MATCH_PATHS } from '../../config/featureHides';
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Layout from '../Layout/Layout';
import { useNavT } from '../../nav/useNavT';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useInHub } from '../../nav/useInHub';
import { themesOpen, wallsOpen } from '../../nav/navModel';
import FeatureUnavailable from '../../components/activation/FeatureUnavailable';

export default function ScheduleHome() {
  const { t } = useTranslation();
  const { companyDetails } = useSelector((state) => state.global);
  const features = companyDetails?.package?.features;
  const nt = useNavT();
  const inHub = useInHub();
  const { has, features: pkgFeatures } = useEntitlements();
  const ctx = { has, features: pkgFeatures };
  const allTiles = [
    { id: 'themes', label: nt('nav6.sched.title', 'Theme Schedule'), descText: nt('nav6.sched.home_themes_desc', 'Switch the whole theme (background + music + corners) by time of day.'), icon: 'pi pi-calendar', to: '/schedule/themes' },
    { id: 'locations', label: nt('nav6.sched.locations', 'Locations'), descText: nt('nav6.sched.home_locations_desc', 'Manage your branches and see which screens are in each location.'), icon: 'pi pi-map-marker', to: '/schedule/locations' },
    { i18n: 'nav.schedule_matches',    icon: 'pi pi-flag-fill', desc: 'schedule.tiles.matches',    to: '/apps/match-scheduler' },
    { i18n: 'nav.schedule_birthday',   icon: 'pi pi-gift',      desc: 'schedule.tiles.birthday',   to: '/apps/birthday' },
    { i18n: 'nav.schedule_happy_hour', icon: 'pi pi-bolt',      desc: 'schedule.tiles.happy_hour', to: '/apps/happy-hour' },
    // Rollout gate (venue_overlay_promos), not a plan tier — a company whose
    // player fleet hasn't been updated yet should never see this tile at
    // all, matching RoutesContainer.jsx's route-level gate on the same key.
    ...(features?.venue_overlay_promos
      ? [{ i18n: 'nav.schedule_promos', icon: 'pi pi-images', desc: 'schedule.tiles.promos', to: '/apps/promos' }]
      : []),
    { i18n: 'nav.schedule_spin_win',   icon: 'pi pi-star',      desc: 'schedule.tiles.spin_win',   to: '/apps/spin-win' },
    { i18n: 'nav.schedule_prayer',     icon: 'pi pi-moon',      desc: 'schedule.tiles.prayer',     to: '/apps/prayer' },
  ];

  // New navigation: «القادم» is the first tab of the الجدول hub, so this page lists only the SCHEDULING pages.
  // The other tiles moved to their own place: matches (own sidebar item), birthday / happy hour / promos /
  // spin (لحظات على الشاشة), prayer (أدوات الشاشة), locations (الحساب). Off: the list is exactly as before.
  const tiles = nt.on
    ? [
        ...(themesOpen(ctx) ? [allTiles.find((x) => x.id === 'themes')] : []),
        ...(wallsOpen(ctx) ? [{ id: 'walls', label: nt('nav6.tab.walls', 'Video wall schedule'), descText: nt('nav6.sched.home_walls_desc', 'Schedule playlists on your video walls by day and time.'), icon: 'pi pi-th-large', to: '/apps/video-wall/schedule' }] : []),
      ].filter(Boolean)
    : allTiles.filter((x) => !(fixtureMatchHidden() && FIXTURE_MATCH_PATHS.includes(x.to)));

  // New navigation: this page is only a menu of scheduling pages. One page = go straight to it (no one-tile page);
  // none = nothing in the package to show (the sidebar item is hidden for such a package, this is a deep link).
  if (nt.on && tiles.length === 1) return <Navigate replace to={tiles[0].to} />;
  if (nt.on && tiles.length === 0) return <Layout padding={false}><FeatureUnavailable /></Layout>;

  return (
    <Layout>
      <div className="container-fluid py-3">
        {!inHub && (<>
          <h2 className="pp-page-title mb-2">
            <i className="pi pi-calendar" /> {t('nav.schedule')}
          </h2>
          <p className="text-muted mb-4">{t('schedule.subtitle')}</p>
        </>)}
        <div className="row g-3">
          {tiles.map((tile) => (
            <div key={tile.i18n || tile.to} className="col-12 col-md-6 col-xl-4">
              <Link to={tile.to} className="text-decoration-none">
                <div className="pp-card p-4 h-100 d-flex flex-column gap-2 hover-lift">
                  <span className="pp-empty-icon" style={{ width: 48, height: 48, fontSize: '1.3rem' }}>
                    <i className={tile.icon} />
                  </span>
                  <h5 className="mb-0 mt-2" style={{ color: 'var(--pp-text-primary)', fontWeight: 700 }}>
                    {tile.label || t(tile.i18n)}
                  </h5>
                  <p className="mb-0 small text-muted">{tile.descText || t(tile.desc)}</p>
                  <span className="small fw-semibold mt-auto" style={{ color: 'var(--pp-btn-honey)' }}>
                    {t('common.open')} <i className="pi pi-arrow-right" style={{ fontSize: '0.75rem' }} />
                  </span>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
